<template>
  <div>
    <div v-if="!isBenchmarksView">
      <FilterAccordion
        v-for="(dimensions, category) in categoryMap"
        class="accordion-container"
        :key="category"
        :value="valuesMap[category]"
        :category="category"
        :dimensions="dimensions"
        :dateRange="dateRange"
        :brandIds="selectedBrandIds"
        :portfolioBrands="enabledBrandsSummary"
        :useSalesInsightsIfAvailable="useSalesInsightsIfAvailable"
        :comparisonRange="comparisonRange"
        :singleSelectBrandId="singleSelectBrandId"
        @error="$emit('error', $event)"
        @input="updateValue"
      ></FilterAccordion>
    </div>
    <div v-else>
      <FilterSelect
        v-for="dimension in localDimensions"
        class="select-container"
        :key="dimension.key"
        :value="getSelectedValue(dimension, localValue)"
        :dimension="dimension"
        :dateRange="dateRange"
        :comparisonRange="comparisonRange"
        :singleSelectBrandId="singleSelectBrandId"
        :selectedCategoryId="selectedCategoryId"
        :selectedSubcategoryIds="selectedSubcategoryIds"
        :portfolioBrands="activeViewEnabledBrands"
        :isMultiSelect="!isBrandDimension(dimension) && !isCategoryDimension(dimension)"
        :useSalesInsightsIfAvailable="useSalesInsightsIfAvailable"
        @error="$emit('error', $event)"
        @input="updateValue"
        @updateSingleSelectBrand="$emit('updateSingleSelectBrandId', $event)"
        @updateSelectedCategory="$emit('updateSelectedCategory', $event)"
        @updateSelectedSubcategories="$emit('updateSelectedSubcategories', $event)"
      >
      </FilterSelect>
    </div>
  </div>
</template>
<script>
import { mapGetters, mapState } from 'vuex';
import FilterAccordion from './FilterAccordion.vue';
import FilterSelect from './FilterSelect.vue';
import {
  getFormattedDateRange,
} from '../../../services/dashboard/dates/datesService.js';

export default {
  props: {
    value: Array,
    dimensions: Array,
    useSalesInsightsIfAvailable: Boolean,
    singleSelectBrandId: String,
    selectedCategoryId: {
      type: [Number, String],
      default: '',
    },
    selectedSubcategoryIds: Array,
  },
  components: {
    FilterAccordion,
    FilterSelect,
  },
  data() {
    return {
      localValue: this.value,
    };
  },
  computed: {
    ...mapState({
      storedStartDate: state => state.dashboard.reportRequest.startDate,
      storedEndDate: state => state.dashboard.reportRequest.endDate,
      storedComparisonRange: state => state.dashboard.reportRequest.comparisonRange,
      activeView: state => state.main.activeView,
      portfolioBrands: state => state.dashboard.portfolioBrands,
      allReportFields: state => state.dashboard.availableReportFields.allReportFields,
      showNewBrandSelector: state => state.main.showNewBrandSelector,
    }),
    ...mapGetters([
      'activeViewEnabledBrands',
      'selectedBrands',
      'selectedBrandIds',
    ]),
    enabledBrandsSummary() {
      return this.activeViewEnabledBrands.map(this.getBrandSummaryObj);
    },
    selectedBrandsSummary() {
      return this.selectedBrands.map(this.getBrandSummaryObj);
    },
    brandNameDimension() {
      return this.allReportFields.find(field => field.value === 'brandName');
    },
    localDimensions() {
      return this.showNewBrandSelector
        ? this.dimensions
        : [ this.brandNameDimension, ...this.dimensions ];
    },
    categoryMap() {
      return this.localDimensions.reduce((map, d) => {
        map[d.category] = map[d.category] ? map[d.category].concat([d]) : [d];

        return map;
      }, {});
    },
    dateRange() {
      return getFormattedDateRange(
        this.storedStartDate,
        this.storedEndDate,
      );
    },
    comparisonRange() {
      return getFormattedDateRange(
        this.storedComparisonRange.start,
        this.storedComparisonRange.end,
      );
    },
    valuesMap() {
      return this.localDimensions.reduce((map, d) => {
        const individualMap = map[d.category] || {};

        individualMap[d.value] = this.getSelectedValue(d, this.localValue);
        map[d.category] = individualMap;

        return map;
      }, {});
    },
    isBenchmarksView() {
      return this.activeView === 'Benchmarks';
    },
  },
  methods: {
    updateValue(filterValuesMap) {
      const newFilters = this.getFiltersFromMap(filterValuesMap);

      for (const newFilter of newFilters) {
        const isAlreadyPresent = this.localValue.some(
          filter => this.isFiltersEqual(filter, newFilter),
        );
        const isNewFilterValue = newFilter.value.length > 0;

        if (isAlreadyPresent) {
          const filterFunc = isNewFilterValue ? this.addFilter : this.removeFilter;

          this.localValue = this.localValue.reduce((filters, filter) => {
            return filterFunc(filters, filter, newFilter);
          }, []);

          continue;
        }

        if (isNewFilterValue) {
          this.localValue = this.localValue.concat(newFilter);
        }
      }

      this.$emit('input', this.localValue);
    },
    getFiltersFromMap(filterValuesMap) {
      return Object.keys(filterValuesMap).map(key => {
        const filterValuePairs = filterValuesMap[key];
        const filterValues = filterValuePairs && filterValuePairs.length
          ? filterValuePairs.map(({ value }) => value)
          : [];

        return {
          key: key,
          value: filterValues,
          operator: 'includes',
        };
      });
    },
    getSelectedValue(dimension, filters) {
      const isBrandDimension = this.isBrandDimension(dimension);

      if (isBrandDimension
        && !this.showNewBrandSelector
        && !filters.some(f => f.key === 'brandName')
      ) {
        return this.selectedBrandsSummary;
      }

      const keyFunc = this.getKeyFunc(dimension);

      return filters.reduce((selected, { key, value }) => {
        if (dimension.value !== key) {
          return selected;
        }

        const filterValue = value === undefined ? [null] : value;
        const filterDisplay = filterValue.map(v => {
          const isUndefinedOrNull = v === undefined || v === null;

          if (isBrandDimension) {
            return this.enabledBrandsSummary.find(b => b.value === v);
          }

          return {
            key: isUndefinedOrNull ? 'null' : keyFunc(v),
            value: v,
          };
        });

        return selected.concat(filterDisplay);
      }, []);
    },
    getKeyFunc(dimension) {
      switch (dimension.value) {
        case 'isBrandSku':
          return (v) => (v.toString());
        default:
          return (v) => (v);
      }
    },
    getBrandSummaryObj(brand) {
      const { key, value, portfolioName } = brand;

      return { key, value, category: portfolioName };
    },
    addFilter(filters, currentFilter, newFilter) {
      const addFilter = this.isFiltersEqual(currentFilter, newFilter) ? newFilter : currentFilter;

      return filters.concat(addFilter);
    },
    removeFilter(filters, currentFilter, newFilter) {
      return this.isFiltersEqual(currentFilter, newFilter)
        ? filters
        : filters.concat(currentFilter);
    },
    isFiltersEqual(f1, f2) {
      return f1.key === f2.key;
    },
    isBrandDimension(dimension) {
      return dimension.value === 'brandName';
    },
    isCategoryDimension(dimension) {
      return dimension.value === 'category';
    },
  },
  watch: {
    value() {
      this.localValue = this.value;
    },
  },
};
</script>
<style scoped>
.accordion-container {
  margin-bottom: 16px;
}

.select-container {
  padding: 12px 0px;
}
</style>
